import { useMemo, useState } from "preact/hooks";
import { t } from "../i18n";
import { hrefFor } from "../app/router";
import type { StaticEvidenceProvider } from "../data/provider";
import type { DisciplineId, Technology } from "../data/schema";
import { IconArrow } from "./icons";

const TABS: DisciplineId[] = ["biology", "chemistry", "interdisciplinary"];

const PREFERRED: Record<DisciplineId, string[]> = {
  biology: ["tech-bioinformatics", "tech-biotechnology", "tech-biomolecular-analysis", "tech-pharma-biotech", "tech-virtual-labs"],
  chemistry: ["tech-organic-synthesis", "tech-materials-analysis", "tech-green-chemistry", "tech-advanced-analytical"],
  interdisciplinary: [
    "tech-ai-natural-sciences",
    "tech-scientific-data-analytics",
    "tech-vle-mooc",
    "tech-drug-discovery",
  ],
};

function pickTechs(provider: StaticEvidenceProvider, discipline: DisciplineId): Technology[] {
  const all = provider.technologies().filter((tech) => tech.disciplineIds.includes(discipline));
  const preferred = PREFERRED[discipline]
    .map((id) => all.find((tech) => tech.id === id))
    .filter((tech): tech is Technology => Boolean(tech));
  const rest = all.filter((tech) => !preferred.some((p) => p.id === tech.id));
  return [...preferred, ...rest].slice(0, 5);
}

export function DiscoveryPreview({ provider }: { provider: StaticEvidenceProvider }) {
  const [tab, setTab] = useState<DisciplineId>("biology");
  const [activeId, setActiveId] = useState<string | undefined>();
  const techs = useMemo(() => pickTechs(provider, tab), [provider, tab]);
  const active = techs.find((tech) => tech.id === (activeId ?? techs[0]?.id)) ?? techs[0];
  const skills = active
    ? active.relatedSkillIds.map((id) => provider.skillById(id)).filter(Boolean).slice(0, 4)
    : [];
  const careers = active
    ? active.relatedCareerIds.map((id) => provider.careerById(id)).filter(Boolean).slice(0, 3)
    : [];

  return (
    <section class="obs-section" aria-labelledby="obs-discover-title">
      <div class="obs-section-head">
        <p class="obs-eyebrow">{t("discover.eyebrow")}</p>
        <h2 id="obs-discover-title">{t("discover.title")}</h2>
        <p class="obs-section-lead">{t("discover.body")}</p>
      </div>
      <div class="obs-tabs" role="tablist" aria-label={t("discover.tabs")}>
        {TABS.map((id) => (
          <button
            key={id}
            class="obs-tab"
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => {
              setTab(id);
              setActiveId(undefined);
            }}
          >
            {t(`radar.${id}`)}
          </button>
        ))}
      </div>
      <div class="obs-discover">
        <ul class="obs-rel">
          {techs.map((tech) => (
            <li key={tech.id}>
              <button
                class={`obs-rel-node${active?.id === tech.id ? " is-active" : ""}`}
                type="button"
                onClick={() => setActiveId(tech.id)}
              >
                <span class="obs-rel-dot" />
                {tech.name}
              </button>
            </li>
          ))}
        </ul>
        {active ? (
          <article class="obs-rel-detail">
            <p class="obs-eyebrow">{t("discover.area")}</p>
            <h3>{active.name}</h3>
            <p>{active.shortDefinition}</p>
            {skills.length > 0 && (
              <p class="obs-rel-meta">
                <strong>{t("radar.relatedSkills")}:</strong>{" "}
                {skills.map((s, i) => (
                  <span key={s!.id}>
                    {i > 0 ? " · " : ""}
                    <a href={hrefFor("/radar", `?type=skill&entity=${s!.id}`)}>{s!.name}</a>
                  </span>
                ))}
              </p>
            )}
            {careers.length > 0 && (
              <p class="obs-rel-meta">
                <strong>{t("radar.relatedCareers")}:</strong>{" "}
                {careers.map((c, i) => (
                  <span key={c!.id}>
                    {i > 0 ? " · " : ""}
                    <a href={hrefFor("/radar", `?type=career&entity=${c!.id}`)}>{c!.title}</a>
                  </span>
                ))}
              </p>
            )}
          </article>
        ) : null}
      </div>
      <p class="obs-section-cta">
        <a
          class="obs-btn obs-btn-text"
          href={hrefFor("/radar", active ? `?type=technology&entity=${active.id}` : "")}
        >
          {t("discover.cta")}
          <IconArrow size={18} />
        </a>
      </p>
    </section>
  );
}
